// Importar apiBase instala el interceptor de 503 (mantenimiento) del core.
import { getApiBase, getHeaders } from "@educaplay/app-core/apiBase";
import { getCookie, setCookie } from "@educaplay/app-core/functions";
import { getDataCheckNewPaymentMethod, getDataNewPaymentMethod } from "./paymentMethods";
import { getEnvironment } from "./environment";
import { v4 as uuid } from 'uuid';
import { PAYMENT_STATUS } from "./constants";

const getBillingHeaders = (lang) => {
    const { tokenID, checkoutToken } = getEnvironment();
    const headers = getHeaders(lang, 'none', tokenID);
    return checkoutToken ? { ...headers, 'X-Checkout-Token': checkoutToken } : headers;
}

const withRenewalToken = (params) => {
    const { renewalToken } = getEnvironment();
    return renewalToken ? { ...params, renewalToken } : params;
}

const requestRenewal = async (data, method, path, params = {}) => {
    const query = new URLSearchParams(withRenewalToken(method == 'GET' ? params : {})).toString();
    try {
        const response = await fetch(getApiBase(data.environment) + '/renewals/' + path + (query ? '?' + query : ''), {
            method: method,
            headers: getBillingHeaders(data.lang),
            credentials: 'include',
            body: (method == 'GET') ? undefined : new URLSearchParams(withRenewalToken(params))
        });
        if (response.status == 401) {
            return { status: 'error', errors: ['permiso'] };
        }
        return await response.json();
    } catch (error) {
        return { status: 'error', errors: ['renewal_unknown'] };
    }
}

export const getPendingRenewal = (data) => requestRenewal(data, 'GET', 'pending/');

export const retryRenewal = (data) => requestRenewal(data, 'POST', 'pending:retry/', { paymentMethod: data.paymentMethod });

export const confirmRenewalRetry = (data) => requestRenewal(data, 'POST', 'pending:confirm/', { paymentIntent: data.paymentIntent });

export const getCountries = async (data) => {
    let responseData;
    try {
        let url = getApiBase(data.environment) + '/countries/';
        const response = await fetch(url, {
            headers: getHeaders(data.lang),
            credentials: 'include'
        });
        if (response.status == 200) {
            const mResponse = await response.json();
            responseData = {
                countries: [],
                provinces: []
            };
            Object.values(mResponse.countries).forEach(country => {
                responseData.countries[country.id] = country.name;
                if (country.provinces !== undefined) {
                    responseData.provinces[country.id] = [];
                    Object.values(country.provinces).forEach(province => {
                        responseData.provinces[country.id][province] = province;
                    });
                }
            });
        } else {
            responseData = false;
        }
    } catch (error) {
        responseData = false;
    }
    return responseData;
}

export const checkPaymentMethod = async (data) => {
    let responseData;
    try {
        const url = getApiBase(data.environment) + '/paymentMethods/' + data.paymentMethod + '/?' + new URLSearchParams(withRenewalToken({ check: 'true' })).toString();
        const response = await fetch(url, {
            headers: getBillingHeaders(),
            credentials: 'include'
        });
        if (response.status == 200) {
            responseData = await response.json();
            delete responseData._links;
        } else {
            responseData = false;
        }
    } catch (error) {
        responseData = false;
    }
    return responseData;
}

export const getPaymentMethods = async (data) => {
    let responseData;
    try {
        const url = getApiBase(data.environment) + '/paymentMethods/?email=' + encodeURIComponent(data.email) + '&check=true';
        const response = await fetch(url, {
            headers: getBillingHeaders(),
            credentials: 'include'
        });
        if (response.status == 200) {
            const mResponse = await response.json();
            responseData = [];
            Object.values(mResponse.paymentMethods).forEach(paymentMethod => {
                if (paymentMethod.status == PAYMENT_STATUS.SUCCESS || paymentMethod.status == PAYMENT_STATUS.PROCESSING) {
                    delete paymentMethod._links;
                    responseData.push(paymentMethod);
                }
            });
        } else {
            responseData = false;
        }
    } catch (error) {
        responseData = false;
    }
    return responseData;
}

export const getBilling = async (data) => {
    let responseData;
    try {
        let url = getApiBase(data.environment) + '/billing/';
        if (data.budget != '') {
            url += '?budget=' + data.budget
        }
        const response = await fetch(url, {
            headers: getBillingHeaders(getEnvironment().lang),
            credentials: 'include'
        });
        if (response.status == 200) {
            responseData = await response.json();
            responseData.status = 'success';
            delete responseData._links;
        } else {
            data = await response.json();
            responseData = {
                status: 'error',
                error: data.errors[0],
            };
        }
    } catch (error) {
        responseData = {
            status: 'error',
            error: 'carga_facturacion'
        }
    }
    return responseData;
}

export const getBudget = async (data) => {
    let responseData;
    try {
        const url = getApiBase(data.environment) + '/budgets/' + data.budget + '/';
        const response = await fetch(url, {
            headers: getBillingHeaders(),
            credentials: 'include'
        });
        if (response.status == 200) {
            responseData = await response.json();
            delete responseData._links;
        } else {
            responseData = false;
        }
    } catch (error) {
        responseData = false;
    }
    return responseData;
}

export const getInvoices = async (data) => {
    let responseData;
    try {
        let url = getApiBase(data.environment) + '/invoices/';
        if (data.budget != '') {
            url += '?budget=' + data.budget
        }
        const response = await fetch(url, {
            headers: getBillingHeaders(),
            credentials: 'include'
        });
        if (response.status == 200) {
            const mResponse = await response.json();
            responseData = mResponse.invoices;
        } else {
            responseData = false;
        }
    } catch (error) {
        responseData = false;
    }
    return responseData;
}

const requestInvoiceRecipients = async (data, method, path, parameters) => {
    try {
        const response = await fetch(getApiBase(data.environment) + '/billing/' + path, {
            method: method,
            credentials: 'include',
            headers: getHeaders(data.lang, 'none', data.tokenID),
            body: (parameters === undefined) ? undefined : new URLSearchParams(parameters)
        });
        const responseJSON = await response.json();
        if (responseJSON.recipients === undefined) {
            return { success: false, errors: [] };
        }
        return {
            success: response.status == 200,
            errors: responseJSON.errors ?? [],
            recipients: responseJSON.recipients,
            maxRecipients: responseJSON.maxRecipients
        };
    } catch (error) {
        return { success: false, errors: [] };
    }
}

export const setInvoiceByEmail = (data) => requestInvoiceRecipients(data, 'PUT', 'invoiceByEmail/', { enabled: data.enabled });

export const getInvoiceRecipients = (data) => requestInvoiceRecipients(data, 'GET', 'invoiceRecipients/');

export const addInvoiceRecipient = (data) => requestInvoiceRecipients(data, 'POST', 'invoiceRecipients/', { email: data.email });

export const removeInvoiceRecipient = (data) => requestInvoiceRecipients(data, 'DELETE', 'invoiceRecipients/' + data.id + '/');

export const generateBudget = async (data) => {
    let responseData = false;
    try {
        const url = getApiBase(data.environment) + '/budgets/';
        const parameters = {
            user: data.user,
            id: data.selectedLicense.id,
            periodicity: data.selectedLicense.periodicity,
            licenses: data.selectedLicense.licenses,
            invoice: data.selectedLicense.wantsAnInvoice,
            vat_number: data.selectedLicense.vat_number,
            name: data.selectedLicense.name,
            address: data.selectedLicense.address,
            town: data.selectedLicense.town,
            province: data.selectedLicense.province,
            zip_code: data.selectedLicense.zip_code,
            country: data.selectedLicense.country,
            budget: data.budget,
            origin: getEnvironment().origin
        };
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getBillingHeaders(),
            body: new URLSearchParams(parameters)
        });
        if (response.status == 200) {
            const responseJSON = await response.json();
            if (responseJSON.code != '') {
                responseData = {
                    code: responseJSON.code,
                    vat: responseJSON.vat
                };
            }
        }
    } catch (error) {
    }
    return responseData;
}

export const acceptBudget = async (data) => {
    let responseData = {
        status: 'error',
        errors: false,
        data: false
    };
    try {
        const url = getApiBase(data.environment) + '/budgets/' + data.budget + ':accept/';
        const parameters = {
            paymentMethod: data.paymentMethod,
            origin: getEnvironment().origin
        };
        const response = await fetch(url, {
            method: 'PUT',
            credentials: 'include',
            headers: getBillingHeaders(data.lang),
            body: new URLSearchParams(parameters)
        });
        const responseJSON = await response.json();
        if (response.status == 200) {
            responseData = {
                ...responseData,
                status: responseJSON.status,
                data: (responseJSON.data === undefined) ? false : responseJSON.data
            }
        } else {
            responseData = {
                ...responseData,
                status: 'error',
                errors: responseJSON.errors
            }
        }
    } catch (error) {
        responseData = {
            ...responseData,
            status: 'error',
            errors: ['unknown']
        }
    }
    return responseData;
}

const getPaymentMethodDataCache = {};

export const getPaymentMethodData = async (data) => {
    const url = getApiBase(data.environment) + '/paymentMethods:data/';
    const dataPaymentMethod = await getDataCheckNewPaymentMethod(data.paymentMethod.type, data.paymentMethod.data);
    if (getPaymentMethodDataCache[dataPaymentMethod] === undefined) {
        const parameters = {
            type: data.paymentMethod.type,
            data: dataPaymentMethod
        };
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getBillingHeaders(),
            body: new URLSearchParams(withRenewalToken(parameters))
        });
        const responseData = await response.json();
        getPaymentMethodDataCache[dataPaymentMethod] = responseData;
        return responseData;
    } else {
        return getPaymentMethodDataCache[dataPaymentMethod];
    }
}

export const createPaymentMethod = async (data) => {
    let responseData = false;
    let payerIdentification = getCookie('payer');
    if (payerIdentification === undefined) {
        payerIdentification = uuid();
        setCookie('payer', payerIdentification, data.environmentCookies);
    }
    try {
        const url = getApiBase(data.environment) + '/paymentMethods/';
        const dataPaymentMethod = await getDataNewPaymentMethod(data.paymentMethod.type, data.paymentMethod.data, data.environment);
        const parameters = {
            email: data.email,
            type: data.paymentMethod.type,
            data: dataPaymentMethod,
            budget: data.budget,
            return: data.return,
            showConsent: data.showConsent,
            payer: payerIdentification
        };
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getBillingHeaders(data.lang),
            body: new URLSearchParams(withRenewalToken(parameters))
        });
        const responseJSON = await response.json();
        responseData = responseJSON;
    } catch (error) {
    }
    return responseData;
}

export const markBudgetAsSeen = async (data) => {
    try {
        const url = getApiBase(data.environment) + '/budgets/' + data.budget + ':used/';
        await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getBillingHeaders()
        });
    } catch (error) {
    }
}

export const cancelPlan = async (data) => {
    let responseData = 'error';
    try {
        const url = getApiBase(data.environment) + '/billing/';
        let response = false;
        if (data.budget != '') {
            const parameters = {
                email: data.email,
                budget: data.budget,
                reason: data.reason || ''
            };
            response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: getBillingHeaders(data.lang),
                body: new URLSearchParams(parameters)
            });
        } else {
            response = await fetch(url, {
                method: 'DELETE',
                credentials: 'include',
                headers: getHeaders(data.lang, 'none', data.tokenID),
                body: new URLSearchParams({ reason: data.reason || '' })
            });
        }
        if (response.status == 200) {
            const responseJSON = await response.json();
            responseData = responseJSON.status;
        }
    } catch (error) {
    }
    return responseData;
}

export const setCancelFeedback = async (data) => {
    let responseData;
    try {
        const url = getApiBase(data.environment) + '/surveys/cancel/';
        const parameters = {
            reason: data.reason,
            userId: data.userId || '',
        }
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getHeaders(data.lang, 'none', data.tokenID),
            body: new URLSearchParams(parameters)
        });
        if (response.status == 200) {
            responseData = {
                success: true
            }
        } else {
            const responseErrors = await response.json();
            responseData = {
                success: false,
                errors: responseErrors.errors
            }
        }
    } catch (error) {
        responseData = {
            success: false,
            errors: []
        }
    }
    return responseData;
}
const checkNameVatCache = new Map();

export const checkNameVat = async (data) => {
    const cacheKey = `${data.vat_number}_${data.name}`;
    if (checkNameVatCache.has(cacheKey)) {
        return checkNameVatCache.get(cacheKey);
    }

    try {
        const url = getApiBase(data.environment) + '/checknif/';

        const formData = new FormData();
        formData.append('vat_number', data.vat_number); 
        formData.append('name', data.name);

        // Sin Content-Type explicito: el body es FormData y el navegador debe poner
        // el multipart con su boundary.
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'include',
            headers: getBillingHeaders(data.lang),
            body: formData
        });
        if (response.status == 200) {
            const responseJSON = await response.json();
            checkNameVatCache.set(cacheKey, responseJSON.success);
            return responseJSON.success;
        }
        else {
            return false;
        }
    } catch (error) {
        return false;
    }
}
